import React, { FC, useCallback, useMemo } from "react";
import { WrapperSection } from "@ldp-web/component-library/ui/atoms/section";
import {
  MiniProductTileCarousel,
  MiniProductTileCarouselProps,
} from "@ldp-web/component-library/ui/organisms/mini-product-tile-carousel";
import { useFeatureFlags } from "@ldp/common-contexts";
import { useSDMCartContext, useSDMRouterContext } from "@ldp/sdm-contexts";
import {
  TSDMDTMProductAnalyticsContext,
  getSDMSnowplowAddToCartContext,
  useAddToCartTrackEvent,
} from "@ldp/sdm-analytics-utils";

import { HeaderGroupComponent } from "../header-group/header-group.component";
import {
  IHeaderGroupComponentProps,
  ISDMProductTileData,
  TSDMCta,
} from "@ldp/common-types";
import { useRedirectLink } from "../../utils";
import { useGetA11yCartMessages } from "../../hooks";

export interface IMiniProductTileCarouselComponentProps
  extends Omit<
    MiniProductTileCarouselProps,
    "cta" | "trackingFn" | "updateCart"
  > {
  callToAction: TSDMCta;
  contentId?: string;
  componentId?: string;
  headerGroup?: IHeaderGroupComponentProps;
  headline?: string;
  /** id of component */
  id?: string;
}

/** Renders Stacked product carousel component */
export const MiniProductTileCarouselComponent: FC<
  IMiniProductTileCarouselComponentProps
> = (props) => {
  const {
    headline,
    productTiles,
    displayText1,
    callToAction,
    headerGroup,
    numberOfRows = 2,
    id,
  } = props;
  const { addToCart, isLoading: isCartLoading, entries } = useSDMCartContext();
  const { getFeatureFlags } = useFeatureFlags();
  const { EnableHeliosSnowplow } = getFeatureFlags();
  const trackFn = useAddToCartTrackEvent(EnableHeliosSnowplow);
  const a11yCartMessages = useGetA11yCartMessages();
  const compData = useMemo(
    () => ({
      id: props.contentId || null,
      type: props.componentId || null,
      name: headline || null,
    }),
    [props.contentId, props.componentId, headline]
  );

  // Create a wrapper over the tracking function to pass productTiles
  const trackFnWrapper = useCallback(
    (type: string, context: unknown): void => {
      const contextData = getSDMSnowplowAddToCartContext(
        context as TSDMDTMProductAnalyticsContext,
        productTiles as unknown as ISDMProductTileData[],
        compData
      );
      if (contextData) {
        trackFn(type, contextData);
      }
    },
    [productTiles, trackFn, compData]
  );

  const router = useSDMRouterContext();
  const redirectToPage = useRedirectLink(router);

  const headerGroupProps: IHeaderGroupComponentProps = headerGroup ?? {
    headline: {
      text: headline,
      variantId: "MEDIUM_03",
      callToAction: callToAction,
      clickHandler: redirectToPage,
    },
    paragraph: { text: displayText1, variantId: "REGULAR_01" },
  };

  return (
    <WrapperSection id={id}>
      <HeaderGroupComponent noWrapper {...headerGroupProps} />
      <MiniProductTileCarousel
        skipLinkLabel={headline || ""}
        displayText1={displayText1}
        cta={callToAction}
        productTiles={productTiles}
        isCartLoading={isCartLoading}
        updateCart={addToCart}
        trackingFn={trackFnWrapper}
        entries={entries}
        clickAction={redirectToPage}
        a11yMessages={a11yCartMessages}
        numberOfRows={numberOfRows}
      />
    </WrapperSection>
  );
};

export default MiniProductTileCarouselComponent;
